/**
 * Hook for fetching infrastructure and combined cost metrics (Task 081)
 */

import { useState, useEffect, useCallback } from 'react';
import { adminService } from '@/services/admin/HttpAdminAdapter';
import type {
  CostPeriod,
  InfrastructureCostSummary,
  InfrastructureCostTrendsResponse,
  CombinedCostSummary,
} from '@/services/admin/AdminService';
import Logger from '@/utils/logger';

const logger = Logger.create('useInfrastructureCostMetrics');

export interface UseInfrastructureCostMetricsReturn {
  // Data
  infraSummary: InfrastructureCostSummary | null;
  infraTrends: InfrastructureCostTrendsResponse | null;
  combinedSummary: CombinedCostSummary | null;

  // State
  loading: boolean;
  error: string | null;
  period: CostPeriod;

  // Actions
  setPeriod: (period: CostPeriod) => void;
  refresh: () => void;
}

export function useInfrastructureCostMetrics(
  initialPeriod: CostPeriod = 'month'
): UseInfrastructureCostMetricsReturn {
  const [infraSummary, setInfraSummary] =
    useState<InfrastructureCostSummary | null>(null);
  const [infraTrends, setInfraTrends] =
    useState<InfrastructureCostTrendsResponse | null>(null);
  const [combinedSummary, setCombinedSummary] =
    useState<CombinedCostSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<CostPeriod>(initialPeriod);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      logger.debug(`Fetching infrastructure metrics for period: ${period}`);

      // Fetch all infrastructure cost data in parallel
      const [summary, trends, combined] = await Promise.all([
        adminService.getInfrastructureCostSummary(period),
        adminService.getInfrastructureCostTrends(30),
        adminService.getCombinedCostSummary(period),
      ]);

      setInfraSummary(summary);
      setInfraTrends(trends);
      setCombinedSummary(combined);

      logger.debug('Successfully fetched infrastructure metrics');
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to fetch infrastructure metrics';
      logger.error('Error fetching infrastructure metrics:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    infraSummary,
    infraTrends,
    combinedSummary,
    loading,
    error,
    period,
    setPeriod,
    refresh,
  };
}

export default useInfrastructureCostMetrics;
